/**
 * Signing an access request with a passkey, mounted at
 * /members/step-up/:challengeId.
 *
 * Reached from the portal (a draft request that needs its requester's
 * signature, or Approve on a stage that needs one) or from a link the CLI,
 * the extension or the desktop app opened. The page shows what is being
 * signed, from the challenge's own payload, then runs the browser's passkey
 * prompt and sends the answer. Opened from a link, it also shows the code to
 * compare with the requesting surface; the portal's own visits carry
 * `fromPortal` in router state and leave it out, as there is nothing to match.
 * The Request access modal's "Add one here" also carries `enrolHere`: the page
 * opens at adding a passkey on this device, then signs with it.
 *
 * Nothing on this page comes from the query string or the fragment: the
 * challenge id is the only thing a link carries, and the code shown is always
 * the server's. A test fails if that changes.
 */
import { useToast } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getAssertion, webAuthnAvailable } from "modules/members/webauthn";
import {
  refreshAfterSignature,
  useRefetchChallenge,
  useStepUpChallenge,
  verifyChallenge,
} from "state/members/stepUp";
import { CHALLENGE_NOT_FOUND_MESSAGE } from "state/members/stepUpFailure";

import {
  ADD_HERE,
  addingHere,
  createCeremonyTracker,
  REVIEW,
  stageFor,
  withChallengeStatus,
  type HeldStage,
} from "./challengeStage";
import { hasExpired, msUntil } from "./clock";
import {
  defaultReturn,
  readStepUpState,
  returnLabel as returnLabelFor,
} from "./navigation";
import { signatureKind } from "./payloadProse";
import { signChallenge } from "./signing";
import StepUpView, { type StepUpStage } from "./StepUpView";
import { useKnownAccount } from "./useKnownAccount";

const LOAD_FAILED_MESSAGE = "Check your connection, then try again.";

const TERMINAL: StepUpStage["name"][] = [
  "signed",
  "expired",
  "used",
  "failed",
  "not_found",
  "load_failed",
];

const StepUpPage = () => {
  const { challengeId } = useParams();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const {
    returnTo: requestedReturn,
    actionAssigneeId,
    comments,
    fromPortal,
    enrolHere,
  } = readStepUpState(routerLocation.state);

  const query = useStepUpChallenge(challengeId);
  const reread = useRefetchChallenge();
  const challenge = query.data;
  const account = useKnownAccount(challenge, actionAssigneeId);

  // The route reuses this page across challenges, so the stage is held with
  // the challenge it is for: a new challenge starts at review (or at adding a
  // passkey here, when the visit asks), whatever the last one reached.
  const start = enrolHere ? ADD_HERE : REVIEW;
  const [held, setHeld] = useState<HeldStage>({ challengeId, stage: start });
  const stage = stageFor(held, challengeId, start);
  const setStage = (next: StepUpStage) => setHeld({ challengeId, stage: next });
  const [now, setNow] = useState(() => Date.now());
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Marks a ceremony synchronously on the first click, so a double click
  // cannot start a second one before the "signing" stage has rendered, and
  // knows which challenge is on screen, so a prompt still open for the last
  // one cannot land its result here.
  const [ceremonies] = useState(createCeremonyTracker);
  useEffect(() => ceremonies.show(challengeId), [ceremonies, challengeId]);

  // The server only calls a challenge pending while it is in date, so a
  // pending one that already looks expired here means this clock is wrong.
  // Counting down against it would kill a good challenge on sight.
  const clockTrusted = useMemo(
    () =>
      !challenge ||
      challenge.status !== "pending" ||
      !hasExpired(challenge.expiresAt, query.dataUpdatedAt || Date.now()),
    [challenge, query.dataUpdatedAt],
  );

  const shown: StepUpStage = (() => {
    if (!challengeId) {
      return { name: "not_found", message: CHALLENGE_NOT_FOUND_MESSAGE };
    }
    if (query.isError) {
      return query.error?.status === 404
        ? { name: "not_found", message: query.error.message }
        : {
            name: "load_failed",
            message: query.error?.message ?? LOAD_FAILED_MESSAGE,
          };
    }
    if (!challenge) return { name: "loading" };
    return withChallengeStatus(stage, challenge.status);
  })();

  const counting =
    clockTrusted && (shown.name === "review" || shown.name === "signing");

  useEffect(() => {
    if (!counting) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [counting]);

  // A challenge can run out while it is being read. Signing would fail
  // anyway, so the button goes rather than letting it be pressed and refused.
  // A prompt already open has its own timeout set to the same moment.
  useEffect(() => {
    if (!challenge || !clockTrusted || shown.name !== "review") return;
    if (hasExpired(challenge.expiresAt, now)) setStage({ name: "expired" });
  }, [challenge, clockTrusted, shown.name, now]);

  // Move focus to the heading when the outcome changes, so a keyboard or
  // screen reader lands on the new content.
  useEffect(() => {
    if (TERMINAL.includes(shown.name)) headingRef.current?.focus();
  }, [shown.name]);

  const purpose = challenge?.purpose;
  const returnTo = requestedReturn ?? defaultReturn(purpose);
  const returnLabel = returnLabelFor(purpose, requestedReturn);

  const onSign = async () => {
    if (!challenge || !challengeId || !ceremonies.begin(challengeId)) return;
    const startedFor = challengeId;
    setStage({ name: "signing" });

    const outcome = await signChallenge(
      challenge,
      {
        timeoutMs: clockTrusted
          ? msUntil(challenge.expiresAt, Date.now())
          : undefined,
        // Only an approval has chat buttons to retire; the server refuses an
        // assignee on any other signature. Only an approval takes a note.
        actionAssigneeId:
          challenge.purpose === "approval" ? actionAssigneeId : null,
        comments: challenge.purpose === "approval" ? comments : null,
      },
      { getAssertion, verify: verifyChallenge, reread },
    ).finally(() => ceremonies.end(startedFor));

    // A signature stands wherever the page has gone since.
    if (outcome.kind === "signed") {
      refreshAfterSignature(queryClient, startedFor);
    }
    // The page moved on to another challenge while the prompt was open; this
    // result, and where it would lead, belong to the one it left.
    if (!ceremonies.isShown(startedFor)) return;

    switch (outcome.kind) {
      case "signed": {
        const kind = signatureKind(challenge.payload, challenge.purpose);
        if (requestedReturn) {
          // Started in the portal: go back to where it started, as any other
          // submit or approve would.
          toast({
            status: "success",
            variant: "subtle",
            title:
              kind === "approve" ? "Request approved" : "Request signed and submitted",
          });
          navigate(requestedReturn, { replace: true });
          return;
        }
        setStage({ name: "signed", kind });
        return;
      }
      case "cancelled":
        setStage({ name: "review" });
        return;
      case "not_allowed":
        setStage({ name: "review", notice: "not_allowed" });
        return;
      case "expired":
        setStage({ name: "expired" });
        return;
      case "used":
        setStage({ name: "used", message: outcome.message });
        return;
      case "failed":
        setStage({ name: "failed", message: outcome.message });
    }
  };

  const onEnrolled = async () => {
    if (!challengeId) return;
    let next: StepUpStage;
    try {
      // The new passkey has to join the challenge's allowed credentials.
      await reread(challengeId);
      next = { name: "review", enrolled: true };
    } catch {
      next = { name: "failed", message: LOAD_FAILED_MESSAGE };
    }
    // Only if this challenge is still the one on screen.
    if (ceremonies.isShown(challengeId)) setStage(next);
  };

  const onAddHere = (adding: boolean) => {
    if (stage.name === "review") setStage(addingHere(stage, adding));
  };

  const onRetry = async () => {
    if (shown.name === "load_failed") {
      query.refetch();
      return;
    }
    setStage({ name: "review" });
    // Pick up a change of status (expired, used) before offering to sign again.
    if (challengeId) await reread(challengeId).catch(() => null);
  };

  return (
    <StepUpView
      ref={headingRef}
      stage={shown}
      challenge={challenge}
      account={account}
      now={now}
      clockTrusted={clockTrusted}
      webAuthn={webAuthnAvailable()}
      showCode={!fromPortal}
      returnTo={returnTo}
      returnLabel={returnLabel}
      onSign={onSign}
      onEnrolled={onEnrolled}
      onAddHere={onAddHere}
      onRetry={onRetry}
    />
  );
};

export default StepUpPage;
